import { useCallback, useEffect } from "react";

import purify from "dompurify";
import { useTranslation } from "pro-features/components/WithTranslation";

import { Warning as WarningIcon } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import { Button } from "@similarweb/ui-components";
import { Flex, Text } from "@similarweb/ui-components/foundations";

import {
    DUNNINGS_SITEWIDE_BANNER_CTA_LOCATION,
    DUNNINGS_UPDATE_BILLING_EVENT_SUB_SUB_NAME,
    NT_DUNNINGS_EVENT_NAME,
} from "app/components/Dunnings/dunningsConstants";
import { StyledDunningsSitewideBanner } from "app/components/Dunnings/DunningsSitewideBanner/DunningsSitewideBannerStyled";
import { useDunnings } from "app/components/hooks/useDunnings";
import { usePageTopBannerStripReveal } from "app/components/PageTopBanner/bannerRevealContext";
import { allTrackers } from "app/services/track/track";

export const DunningsSitewideBanner = () => {
    const translate = useTranslation();
    const { onDunningsButtonClicked, isLastPaymentFailedWarning, numberOfFailedPayments } = useDunnings();
    const stripProps = usePageTopBannerStripReveal();

    useEffect(() => {
        allTrackers.dispatchTrackEvent(
            "banner",
            "show",
            `${NT_DUNNINGS_EVENT_NAME}/${DUNNINGS_SITEWIDE_BANNER_CTA_LOCATION}/${numberOfFailedPayments}`,
        );
    }, [numberOfFailedPayments]);

    const onButtonClick = useCallback(
        () =>
            onDunningsButtonClicked(
                `${NT_DUNNINGS_EVENT_NAME}/${DUNNINGS_SITEWIDE_BANNER_CTA_LOCATION}/${DUNNINGS_UPDATE_BILLING_EVENT_SUB_SUB_NAME}`,
                "billing",
            ),
        [onDunningsButtonClicked],
    );

    return (
        <StyledDunningsSitewideBanner
            {...stripProps}
            isLastPaymentFailedWarning={isLastPaymentFailedWarning}
            data-automation="dunnings-sitewide-banner"
        >
            <Flex gap="8px">
                <WarningIcon
                    color={isLastPaymentFailedWarning ? colorsPalettes.red["400_P"] : colorsPalettes.yellow[500]}
                    size="sm"
                />

                <Text
                    fontVariant="body2"
                    dangerouslySetInnerHTML={{
                        // eslint-disable-next-line @typescript-eslint/naming-convention
                        __html: purify.sanitize(
                            translate(
                                isLastPaymentFailedWarning
                                    ? "dunnings.last_warning.top_banner.text"
                                    : "dunnings.top_banner.text",
                            ),
                        ),
                    }}
                />
            </Flex>

            <Button
                onClick={onButtonClick}
                type="outlined"
            >
                {translate("dunnings.top_banner.button_text")}
            </Button>
        </StyledDunningsSitewideBanner>
    );
};
